import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Card from '../elements/Card';
import Icon from './Icon';
import { apiRequest } from '../../utils';

// localStorage flag flipped by the auth flow. We treat its presence as
// "the viewer has a session" purely for UI purposes — the API still
// enforces real auth. See utils.js (login + logout) for where it's set.
const FLAG = 'isLoggedIn';

const linkStyle = {
  textDecoration: 'none', color: 'inherit', display: 'flex',
  justifyContent: 'center', alignItems: 'center', margin: '0', p: '0',
};

const NavBar = ({ active = 'home' }) => {
  const [show, setShow] = useState(() => localStorage.getItem(FLAG) === '1');
  const [role, setRole] = useState(() => sessionStorage.getItem('role'));
  const [model, setModel] = useState(() => sessionStorage.getItem('businessModel'));

  useEffect(() => {
    const sync = () => setShow(localStorage.getItem(FLAG) === '1');
    // Cross-tab login/logout.
    window.addEventListener('storage', sync);
    // Same-tab updates dispatched via window.dispatchEvent(new Event('auth-changed')).
    window.addEventListener('auth-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('auth-changed', sync);
    };
  }, []);

  // Learn the viewer's role so contact persons get the shop instead of the feed, and the
  // business model of the session's unit, because "the feed" is not one screen — see below.
  // The httpOnly JWT can't be read client-side, so we ask the API once and cache the answer in
  // sessionStorage (cleared on logout) to avoid refetching on every page.
  useEffect(() => {
    if (!show) return;
    const cachedRole = sessionStorage.getItem('role');
    const cachedModel = sessionStorage.getItem('businessModel');
    // Both, or neither: an older session that only cached the role has to ask once more.
    if (cachedRole && cachedModel !== null) { setRole(cachedRole); setModel(cachedModel); return; }
    let cancelled = false;
    apiRequest('GET', '/me')
      .then((me) => {
        if (cancelled || !me) return;
        if (me.role) { sessionStorage.setItem('role', me.role); setRole(me.role); }
        // '' is a real answer (no event in the session, no model set) and is cached as one, so
        // this asks once per session rather than on every page.
        const bm = me.business_model || '';
        sessionStorage.setItem('businessModel', bm);
        setModel(bm);
      })
      .catch(() => { /* leave as feed on failure */ });
    return () => { cancelled = true; };
  }, [show]);

  if (!show) return null;

  // Only contact persons buy from a shop; everyone else — players AND Bravos members —
  // sees the social feed here. (A member books Labs via /book/:id, not the contact-person
  // /shop, which 403s for them and bounces to home.)
  const showsShop = role === 'contactPerson';
  // A rep has no shop and no feed — the left tab is the follow-ups they left behind
  // in doorways, which is the only other screen their day has. The home button stays
  // '/home' for everyone; /api/home is what sends this role on to /sales-home.
  const isSales = role === 'sales';
  // Finance has no shop and no feed either. Their left tab is everything they
  // have captured — the one other screen their day has.
  const isFinance = role === 'finance';
  // A supplier has no shop and no feed: its left tab is the list of events it works on.
  const isSupplier = role === 'supplier';
  // ★ WHICH feed depends on the business model of the session's unit. '/feed' is the wall of
  // TEAM mission answers, and under Bravos nobody hands those in — no teams, no submissions — so
  // it is permanently "nothing to show yet" while the feed those people actually have is their
  // company's stream. One nav item, two destinations, until the team wall and the company stream
  // are merged into one feed and this can go.
  const leftTo = isSales ? '/sales-tasks'
    : isSupplier ? '/supplier'
    : (isFinance ? '/finance/list'
      : (showsShop ? '/shop' : (model === 'BRAVOS' ? '/company-feed' : '/feed')));
  const leftIcon = isSales ? 'checkbox'
    : isSupplier ? 'calendar'
    : (isFinance ? 'receipt' : (showsShop ? 'shopping-cart' : 'comment-alt'));
  const leftActive = showsShop
    ? active === 'shop'
    : (active === 'feed' || active === 'notifications');

  return (
    <div className="bottom">
      <Card mb="0" mx="0" p="2">
        <div className="row">
          <NavLink to={leftTo} className="col-4 mt-1 text-center" style={linkStyle}>
            <div className={`col-4 mt-1 text-center ${leftActive ? 'active' : ''}`}>
              <Icon name={leftIcon} />
            </div>
          </NavLink>

          <NavLink to="/home" className="col-4 mt-1 text-center" style={linkStyle}>
            <div className={`col-4 mt-1 text-center ${active === 'home' ? 'active' : ''}`}>
              <Icon name="home" />
            </div>
          </NavLink>

          <NavLink to="/profile" className="col-4 mt-1 text-center" style={linkStyle}>
            <div className={`col-4 mt-1 text-center ${active === 'profile' ? 'active' : ''}`}>
              <Icon name="user" />
            </div>
          </NavLink>
        </div>
      </Card>
    </div>
  );
};

export default NavBar;
